import { useState } from 'react';
import { IToolData } from 'types/products';
import { REFERENCE_MODEL_TYPE } from 'types/global';
import AssetLocationDisplay from 'components/AssetLocationDisplay/AssetLocationDisplay';
import { ICountryDetail } from 'types/countries';
import { truncateText } from 'utils/truncateText';
import { nudosTextcolor } from 'styles/styles.constants';
import NudosToolAdditionalServices from 'components/NudosComponents/NudosToolAdditionalServices/NudosToolAdditionalServices';
import { formatConfigurations } from 'utils/orders';
import { IConfiguration } from 'types/product.configuration.types';
import { BACKOFFICE_MODULES } from 'types/configuration.types';
import { EditableButton } from '../../EditableButton/EditableButton';
import { BordOneToneIcon } from 'components/NudosComponents';
import { getPermissionsByModule } from 'utils/permissions';
import useFiltersState from 'state/useFiltersState';
import './GeneralTab.scss';

/**
 * Componente que muestra la información general de una herramienta
 * @param productData - Datos de la herramienta a mostrar
 */
const GeneralTab = ({
  productData,
  handleShowConditionalEdition,
  handleShowStateEdition,
  handleShowSkuAndSerialEdition,
  isLoading
}: IGeneralTabProps) => {
  const [expandedStates, setExpandedStates] = useState<{
    comment: boolean;
    state: boolean;
    condition: boolean;
  }>({
    comment: false,
    state: false,
    condition: false
  });
  const { status } = productData ?? {};
  const { fileUrl } = status ?? {};

  const { toolsModuleListingSharedModule } = useFiltersState();
  const stockListingTab = toolsModuleListingSharedModule?.key === 2;
  const comment = productData?.comments ?? '';
  const reasonForModificationState = productData?.status?.comments;
  const reasonForModificationCondition = productData?.condition?.comments;
  const truncatedLength = 137;
  const shouldShowButton = comment?.length > truncatedLength;
  const reasonForModificationStateText =
    !!reasonForModificationState && reasonForModificationState
      ? reasonForModificationState
      : 'Se actualizó la condición del equipo tras revisión técnica, confirmando que el MacBook funciona correctamente y solo presenta signos menores de uso estético.';
  const shouldShowButtonState = reasonForModificationStateText?.length > truncatedLength;
  const reasonForModificationConditionText =
    !!reasonForModificationCondition && reasonForModificationCondition
      ? reasonForModificationCondition
      : 'Se actualizó la condición del equipo tras revisión técnica, confirmando que el MacBook funciona correctamente y solo presenta signos menores de uso estético.';
  const shouldShowButtonCondition = reasonForModificationConditionText?.length > truncatedLength;
  // TODO: Se requiere el objeto configurations de parte de back
  const configurationsData = productData?.configurations;
  const formatedConfigurationsData = configurationsData && formatConfigurations(configurationsData as IConfiguration[]);
  const referenceModel = productData?.referenceModel;
  const isExternalProduct = referenceModel === REFERENCE_MODEL_TYPE.EXTERNAL;
  const isStockProduct = referenceModel === REFERENCE_MODEL_TYPE.STOCK;
  const showConfigurations = formatedConfigurationsData && formatedConfigurationsData?.length > 0;
  const showEditButtonSerial = stockListingTab ? (isExternalProduct || isStockProduct)  && productData?.isUploaded : isExternalProduct;

  const getToolType = (): string => {
    switch (productData?.referenceModel) {
      case REFERENCE_MODEL_TYPE.INTERNAL:
        return 'Herramienta Bord';
      case REFERENCE_MODEL_TYPE.EXTERNAL:
        return 'Herramienta externa';
      case REFERENCE_MODEL_TYPE.STOCK:
        return 'Herramienta Stock propio';
      default:
        return 'Tipo no especificado';
    }
  };

  const handleToggle = (section: 'comment' | 'state' | 'condition') => {
    setExpandedStates(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleOpenStatusFileUrl = () => {
    if (!fileUrl) return;
    window?.open(fileUrl, '_blank');
  };

  const validatePermissions = () => {
    const toolsPermissions = getPermissionsByModule({ moduleName: BACKOFFICE_MODULES.TOOLS });
    return toolsPermissions.read || toolsPermissions.edit;
  };

  const hasPermissions = validatePermissions();

  if (!productData) return null;
  return (
    <div className="generalTab">
      <div className="containerInformation">
        <div className="containerInformationItem">
          <p className="title">Cliente:</p>
          <div className="containerInfoValue">
            {productData?.organization?.organizationImage && (
              <img
                src={productData?.organization?.organizationImage}
                alt="Logo de la organización"
                className="organizationImage"
              />
            )}
            <p className="value">{productData?.organization?.organizationName ?? ''}</p>
          </div>
        </div>
      </div>
      <div className="containerInformation">
        <div className="containerInformationItem">
          <p className="title">Estado:</p>
          <div className="flex items-center gap-1">
            {fileUrl && (
              <>
                <span className="statusFileUrl" onClick={handleOpenStatusFileUrl}>
                  Ver PDF
                </span>
                <span className="dividerFileUrl">|</span>
              </>
            )}
            <EditableButton
              titleButton={productData?.status?.name || ''}
              showIcon={hasPermissions}
              onClick={() => hasPermissions && handleShowStateEdition()}
              variantIcon="pencil"
              colorIcon={nudosTextcolor}
              isDisabled={isLoading || !hasPermissions}
              isAvailableClass
            />
          </div>
        </div>
        <div className="containerInfoExtra">
          {!!reasonForModificationState && (
            <>
              <div className="lineDivider" />
              <div className="infoExtra">
                {expandedStates?.state
                  ? reasonForModificationStateText
                  : truncateText(reasonForModificationStateText, truncatedLength)}
                {shouldShowButtonState && (
                  <div className="showMoreComment" onClick={() => handleToggle('state')}>
                    {expandedStates.state ? 'Ver menos' : 'Ver más'}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="containerInformation">
        <div className="containerInformationItem">
          <p className="title">Serial:</p>
          {showEditButtonSerial ? (
            <EditableButton
              titleButton={productData?.serial || 'No especificado'}
              showIcon
              onClick={handleShowSkuAndSerialEdition}
              variantIcon="pencil"
              colorIcon={nudosTextcolor}
              isDisabled={isLoading}
            />
          ) : (
            <p className="value uppercase">{productData?.serial || 'No especificado'}</p>
          )}
        </div>
        <div className="containerInformationItem">
          <p className="title">SKU:</p>
          {showEditButtonSerial ? (
            <EditableButton
              titleButton={productData?.sku || 'No especificado'}
              showIcon
              onClick={handleShowSkuAndSerialEdition}
              variantIcon="pencil"
              colorIcon={nudosTextcolor}
              isDisabled={isLoading}
            />
          ) : (
            <p className="value uppercase">{productData?.sku || 'No especificado'}</p>
          )}
        </div>
        <div className="containerInformationItem">
          <p className="title">Tipo:</p>
          <p className="value">{getToolType()}</p>
        </div>
        <div className="containerInformationItem">
          <p className="title">Ubicación:</p>
          <div className="containerInformationItemContent">
            {!!productData?.inTransit?.origin && (
              <AssetLocationDisplay
                location={productData?.inTransit?.origin}
                locationName={productData?.inTransit?.origin?.locationName || ''}
              />
            )}
            {!!productData?.inTransit?.destination && !!productData?.inTransit?.origin && (
              <BordOneToneIcon variant="arrowRight" standardSize={10} stroke={nudosTextcolor} />
            )}
            {!!productData?.inTransit?.destination && (
              <AssetLocationDisplay
                location={productData?.inTransit?.destination}
                locationName={productData?.inTransit?.destination?.locationName || ''}
              />
            )}
            {!productData?.inTransit?.destination && !productData?.inTransit?.origin && (
              <AssetLocationDisplay
                location={productData?.location}
                locationName={productData?.location?.locationName || ''}
              />
            )}
          </div>
        </div>
      </div>
      <div className="containerInformation">
        <div className="containerInformationItem">
          <p className="title">Condición:</p>
          <EditableButton
            titleButton={productData?.condition?.name || ''}
            showIcon={hasPermissions}
            onClick={() => hasPermissions && handleShowConditionalEdition()}
            variantIcon="pencil"
            colorIcon={nudosTextcolor}
            isDisabled={isLoading || !hasPermissions}
          />
        </div>

        {!!reasonForModificationCondition && (
          <>
            <div className="lineDivider" />
            <div className="infoExtra">
              {expandedStates.condition
                ? reasonForModificationConditionText
                : truncateText(reasonForModificationConditionText, truncatedLength)}
              {shouldShowButtonCondition && (
                <div className="showMoreComment" onClick={() => handleToggle('condition')}>
                  {expandedStates.condition ? 'Ver menos' : 'Ver más'}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* TODO: Se requiere el objeto configurations de parte de back cuando esto se tenga
      implementado, se debe eliminar el comentario y la condición showConfigurations de la linea
      de codigo 192 */}

      {showConfigurations && (
        <div className="containerInformation">
          <div className="containerInformationItem">
            <p className="title">Estado del Sistema:</p>
          </div>
          <div className="lineDivider" />
          {showConfigurations ? (
            <div className="infoExtra">
              <NudosToolAdditionalServices
                additionalServicesOrConfigurationsData={formatedConfigurationsData}
                styling="toolDetails"
              />
            </div>
          ) : (
            <div className="infoExtra">No se han aplicado servicios adicionales</div>
          )}
        </div>
      )}
      <div className="containerInformation">
        <div className="containerInformationItem">
          <p className="title">Comentario:</p>
        </div>
        <div className="lineDivider" />
        <div className="infoExtra">
          {expandedStates.comment ? comment : truncateText(comment, truncatedLength)}
          {shouldShowButton && (
            <div className="showMoreComment" onClick={() => handleToggle('comment')}>
              {expandedStates.comment ? 'Ver menos' : 'Ver más'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;
interface IGeneralTabProps {
  productData: IToolData | null;
  listOfCountries: ICountryDetail[];
  handleShowConditionalEdition: () => void;
  handleShowStateEdition: () => void;
  handleShowSkuAndSerialEdition: () => void;
  isLoading: boolean;
}
